import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchClients, fetchClientsForMultipleUsers, clearClientCache } from './clientSlice';
import {
  Box,
  Typography,
  TextField,
  Paper,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Avatar,
  Collapse
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useResponsiveFonts, truncateName, truncateText, truncatePhoneNumber } from '../../utils/responsiveFonts';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import EventIcon from '@mui/icons-material/Event'; // Added EventIcon for Meetings tab
import { InputAdornment } from '@mui/material';

import MobileBottomNavigation from '../../components/common/MobileBottomNavigation';
import ImportPoliciesDialog from '../../components/ImportPoliciesDialog';
import { getClientCountsByStatus } from '../../utils/countUtils';
import { handleMobileRefresh } from '../../utils/mobileRefresh';
import { useAdminStaffData } from '../../hooks/useAdminStaffData';
import { firebaseCache } from '../../utils/firebaseCache';
const MobileClientList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { clients, loading, initialized } = useSelector((state) => state.clients);
  const { user } = useSelector((state) => state.auth);
  const { userIds, isAdmin, loading: staffLoading } = useAdminStaffData();
  const fonts = useResponsiveFonts();

  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [selectedTab, setSelectedTab] = useState('customers');
  const [displayedClients, setDisplayedClients] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showFabMenu, setShowFabMenu] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const itemsPerPage = 10;
  const contentRef = useRef(null);

  // Refresh handler using shared refresh function
  const handleRefresh = async () => {
    if (isRefreshing) return; // Prevent multiple simultaneous refreshes

    try {
      await handleMobileRefresh({
        userId: user?.uid,
        dispatch,
        setRefreshing: setIsRefreshing
      });
      console.log('✅ ClientList data refreshed');
    } catch (error) {
      console.error('❌ Error refreshing ClientList:', error);
    }
  };

  const tabs = ['customers', 'new-leads'];
  useEffect(() => {
    // Do not set displayed clients until initial data load has completed
    if (!initialized) return;

    // Show ALL clients initially, not just first page
    setDisplayedClients(sortedClients);
    setHasMore(false); // No more to load since we're showing all
  }, [searchTerm, clients.length, selectedTab, initialized]);

  useEffect(() => {
    // Wait for useAdminStaffData to resolve admin status and staff UIDs
    if (staffLoading) return;

    if (isAdmin && userIds.length > 0) {
      console.log('Admin user (Mobile) - fetching aggregated clients:', userIds);
      dispatch(fetchClientsForMultipleUsers(userIds));
    } else if (user?.uid && !isAdmin) {
      // For regular staff, only fetch if not already loaded or if needed
      if (clients.length === 0) {
        console.log('Staff user (Mobile) - fetching own clients');
        dispatch(fetchClients({ agentId: user.uid }));
      }
    }
  }, [user?.uid, dispatch, clients.length, isAdmin, userIds, staffLoading]);

  // Scroll restoration is now handled globally by MobileLayout

  // Filter and sort clients
  const filteredClients = clients.filter((client) => {
    // Filter based on selected tab
    const isNewLead = client.status === "New Lead";
    const tabMatch =
      selectedTab === "customers"
        ? !isNewLead
        : selectedTab === "new-leads"
          ? isNewLead
          : true;

    if (!tabMatch) return false;

    const searchLower = searchTerm.toLowerCase();
    return (
      (client.name || '').toLowerCase().includes(searchLower) ||
      (client.mobile || '').includes(searchTerm) ||
      (client.referredBy || '').toLowerCase().includes(searchLower)
    );
  });

  // Calculate counts for each tab
  const tabCounts = getClientCountsByStatus(clients);

  const sortedClients = filteredClients.sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
    const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
    return dateB - dateA;
  });

  // Initialize displayed clients when filters change
  useEffect(() => {
    // Show ALL clients initially, not just first page
    setDisplayedClients(sortedClients);
    setHasMore(false); // No more to load since we're showing all
  }, [searchTerm, clients.length, selectedTab]);

  // Infinite scroll handler
  const loadMoreClients = useCallback(() => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);
    setTimeout(() => {
      setDisplayedClients(prev => {
        const currentLength = prev.length;
        // Recalculate sortedClients inside the callback to get fresh data
        const filteredClients = clients.filter((client) => {
          const searchLower = searchTerm.toLowerCase();
          const nameMatch = client.name?.toLowerCase().includes(searchLower);
          const mobileMatch = client.mobile?.includes(searchTerm);
          const referredByMatch = client.referredBy?.toLowerCase().includes(searchLower);
          return nameMatch || mobileMatch || referredByMatch;
        });
        const currentSortedClients = filteredClients.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
          const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
          return dateB - dateA;
        });
        const nextClients = currentSortedClients.slice(currentLength, currentLength + itemsPerPage);

        if (nextClients.length > 0) {
          setHasMore(currentLength + nextClients.length < currentSortedClients.length);
          return [...prev, ...nextClients];
        } else {
          setHasMore(false);
          return prev;
        }
      });
      setIsLoading(false);
    }, 300);
  }, [isLoading, hasMore, clients, searchTerm, itemsPerPage]);

  // Scroll event listener for infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

      if (isNearBottom && hasMore && !isLoading) {
        loadMoreClients();
      }
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
      return () => contentElement.removeEventListener('scroll', handleScroll);
    }
  }, [hasMore, isLoading, loadMoreClients]);

  // Show loading state until data is actually loaded
  // Use the `initialized` flag so we don't treat an empty array as "done" prematurely.
  if (loading || !initialized) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <Typography>Loading clients...</Typography>
      </Box>
    );
  }

  const handleClientClick = (client) => {
    navigate(`/client/${client.id}`, {
      state: {
        client: client,
        fromScreen: 'clients'
      }
    });
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleFabMenuOpen = () => {
    setShowFabMenu(true);
  };

  const handleFabMenuClose = () => {
    setShowFabMenu(false);
  };

  return (
    <Box className="mobile-layout mobile-content-container" sx={{
      width: '100vw',
      height: '100vh',
      overflow: 'hidden',
      backgroundColor: '#f5f5f5',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overscrollBehavior: 'none'
    }}>
      {/* Header */}
      <Box sx={{
        backgroundColor: '#0063F7',
        color: 'white',
        px: { xs: 2, sm: 3, md: 4 },
        py: 1,
        height: 70,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1004
      }}>
        <Typography sx={{
          fontSize: fonts.header.xs,
          fontWeight: 600
        }}>
          Clients
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => setShowImportDialog(true)}
            sx={{ color: 'white' }}
          >
            <UploadFileIcon />
          </IconButton>
          <IconButton
            onClick={handleRefresh}
            disabled={isRefreshing}
            sx={{
              color: 'white',
              '&:disabled': {
                opacity: 0.6
              }
            }}
          >
            <RefreshIcon />
          </IconButton>
          <IconButton
            onClick={() => setShowSearch(!showSearch)}
            sx={{ color: 'white' }}
          >
            <SearchIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Search Bar */}
      <Box sx={{
        position: 'fixed',
        top: 70,
        left: 0,
        right: 0,
        zIndex: 1003,
        backgroundColor: showSearch ? 'white' : 'transparent',
        pointerEvents: showSearch ? 'all' : 'none',
        borderBottom: showSearch ? '1px solid #e0e0e0' : 'none',
        transition: 'all 0.3s ease'
      }}>
        <Collapse in={showSearch}>
          <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 1.5, height: 70, boxSizing: 'border-box' }}>
            <TextField
              fullWidth
              placeholder="Search clients by name or phone"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#757575' }} />
                  </InputAdornment>
                )
              }}
            />
          </Box>
        </Collapse>
      </Box>


      {/* Category Tabs Section */}
      <Box sx={{
        position: 'fixed',
        top: showSearch ? 140 : 70,
        left: 0,
        right: 0,
        zIndex: 1002,
        backgroundColor: '#f5f5f5',
        px: 2,
        pt: 2.5, // Increased gap from header
        pb: 2,
        transition: 'top 0.3s ease'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.5 }}>
          {tabs.map((tab) => (
            <Box
              key={tab}
              onClick={() => setSelectedTab(tab)}
              sx={{
                flex: 1,
                py: 0.6,
                px: 1,
                borderRadius: '15px',
                backgroundColor: selectedTab === tab ? '#E3F2FD' : 'white',
                border: `1px solid ${selectedTab === tab ? '#0063F7' : '#e0e0e0'}`,
                cursor: 'pointer',
                textAlign: 'center',
                minWidth: 0,
                transition: 'all 0.2s ease'
              }}
            >
              <Box sx={{
                fontSize: '13.5px !important',
                fontWeight: 500,
                color: selectedTab === tab ? '#0063F7' : '#666',
                textTransform: 'capitalize',
                whiteSpace: 'nowrap',
                letterSpacing: '-0.02em',
                lineHeight: 1.2
              }}>
                {tab === 'customers' ? `Customers (${tabCounts.customers || 0})` : `New Leads (${tabCounts['new-leads'] || 0})`}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>




      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          py: 2,
          pb: 10,
          mt: showSearch ? '205px' : '135px',
          transition: 'margin-top 0.3s ease',
          overflowX: 'hidden',
          backgroundColor: '#f5f5f5'
        }}>
        {displayedClients.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography sx={{ color: '#666', mb: 1 }}>
              No {selectedTab === 'customers' ? 'customers' : 'new leads'} found
            </Typography>
            <Typography sx={{ color: '#999', fontSize: '14px' }}>
              {searchTerm ? 'Try adjusting your search' :
                selectedTab === 'customers' ? 'No customers yet' :
                  'Great time to follow up with new leads!'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ width: '100%' }}>
            {displayedClients.map((client) => (
              <Paper
                key={client.id}
                onClick={() => handleClientClick(client)}
                sx={{
                  p: { xs: 0.8, sm: 1, md: 1.5 },
                  mb: 1.5,
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  width: '100%',
                  minWidth: '100%',
                  maxWidth: '100%',
                  flex: '1 1 100%',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  }
                }}
              >
                {/* Single row layout with avatar, name and phone */}
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  width: '100%',
                  overflow: 'hidden'
                }}>
                  {/* Round Avatar with First Letter */}
                  <Avatar sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: '#0063F7',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: 600
                  }}>
                    {getInitials(client.name)}
                  </Avatar>

                  {/* Name and Phone */}
                  <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <Typography sx={{
                      fontSize: '16px !important',
                      color: '#212121',
                      fontWeight: 600,
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      marginBottom: '1px',
                      '&.MuiTypography-root': {
                        fontSize: '16px !important'
                      }
                    }}>
                      {client.name || 'Unknown Client'}
                    </Typography>
                    <Typography sx={{
                      fontSize: '14px !important',
                      color: '#666',
                      lineHeight: 1.1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      '&.MuiTypography-root': {
                        fontSize: '14px !important'
                      }
                    }}>
                      {client.mobile || 'No phone number'}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography sx={{ fontSize: '14px', color: '#757575' }}>
                  Loading more clients...
                </Typography>
              </Box>
            )}

          </Box>
        )}
      </Box>

      {/* Bottom Navigation */}
      <MobileBottomNavigation isAdmin={isAdmin} contentRef={contentRef} />

      {/* FAB Menu */}
      {showFabMenu && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }} onClick={handleFabMenuClose}>
          <Box sx={{
            backgroundColor: 'white',
            borderRadius: '16px',
            p: 3,
            mx: 4,
            minWidth: '200px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
          }} onClick={(e) => e.stopPropagation()}>
            <Typography sx={{
              fontSize: '18px',
              fontWeight: 600,
              color: '#212121',
              mb: 2,
              textAlign: 'center'
            }}>
              Add New
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                onClick={() => {
                  handleFabMenuClose();
                  navigate('/upload-policy');
                }}
                sx={{
                  justifyContent: 'flex-start',
                  p: 2,
                  borderRadius: '8px',
                  textTransform: 'none',
                  color: '#212121'
                }}
              >
                Upload Policy
              </Button>
              <Button
                onClick={() => {
                  handleFabMenuClose();
                  // Add new policy functionality
                }}
                sx={{
                  justifyContent: 'flex-start',
                  p: 2,
                  borderRadius: '8px',
                  textTransform: 'none',
                  color: '#212121'
                }}
              >
                Add Policy
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Import Policies Dialog */}
      <ImportPoliciesDialog
        open={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onSuccess={() => {
          dispatch(clearClientCache({ agentId: user?.uid }));
          firebaseCache.invalidateAfterWrite('clientlist_write', user?.uid);
          if (isAdmin && userIds.length > 0) {
            dispatch(fetchClientsForMultipleUsers(userIds));
          } else if (user?.uid) {
            dispatch(fetchClients({ agentId: user.uid }));
          }
        }}
      />
    </Box>
  );
};

export default MobileClientList;
